import React from 'react';
import QuestionCard from './QuestionCard';
import TeamJersey from './TeamJersey';
import { AnswerButtonOverUnder } from './shared/AnswerButtonOverUnder';
import TeamLogoBackground from './shared/TeamLogoBackground';

// The threshold + stat name are already in the question copy ("…over 1.5
// Assists?"), so this header duplicated them (and showed the raw stat code,
// e.g. "soccer_assists"). Hidden per customer request — but still shown as a
// fallback when there's no question text (see render). Flip to `true` to force
// it always-on (DKPLAT-400).
const SHOW_STAT_HEADER = false;

export interface OverUnderPickProps {
  questionId: string;
  questionNumber?: number;
  points: number;
  earnedPoints?: number;

  // Match-level markets (e.g. DKPLAT-500 Total) have no player/team subject.
  // When true, the left panel shows the matchup + logos instead of a jersey
  // and player name (which would otherwise fall back to "U. Player").
  isMatchLevel?: boolean;

  // Player data (from question_metadata.grading.config)
  playerName: string;
  playerPosition: string;
  playerJerseyUrl: string; // from question_image_en
  playerTeamLogoUrl?: string; // Team logo to show in top right of player image

  // Stat data (from question_metadata + question_subtext)
  statValue: number;
  statType: string;

  // Match data (from sportMatchData)
  homeTeamName?: string;
  awayTeamName?: string;
  homeTeamLogoUrl?: string; // used by the match-level layout
  awayTeamLogoUrl?: string; // used by the match-level layout
  matchTime: string;

  // Popularity percentages
  overPercentage?: number;
  underPercentage?: number;

  // Selection
  selectedAnswer?: 'over' | 'under';
  onAnswerSelect: (answer: 'over' | 'under' | undefined) => void;

  // Question copy (matches other pick components — without this the card
  // shows only the threshold and stat label, which reads as raw data
  // rather than a question).
  questionText?: string;
  subtext?: string;

  // State
  isCorrect?: boolean;
  disabled?: boolean;
  /** `yesNo` turns the card into a yes/no question: the buttons read Yes and No, Yes on top. */
  questionMetadata?: { dailyDouble?: boolean; yesNo?: { yes: 'over' | 'under' } };
}

const OverUnderPick: React.FC<OverUnderPickProps> = ({
  questionId,
  questionNumber,
  points,
  earnedPoints,
  isMatchLevel = false,
  playerName,
  playerPosition,
  playerJerseyUrl,
  playerTeamLogoUrl,
  statValue,
  statType,
  homeTeamName,
  awayTeamName,
  homeTeamLogoUrl,
  awayTeamLogoUrl,
  matchTime,
  overPercentage,
  underPercentage,
  selectedAnswer,
  onAnswerSelect,
  questionText,
  subtext,
  isCorrect,
  disabled = false,
  questionMetadata,
}) => {
  const isDailyDouble = questionMetadata?.dailyDouble || false;
  const yesAnswer = questionMetadata?.yesNo?.yes;
  const answerOrder: Array<'over' | 'under'> =
    yesAnswer === 'under' ? ['under', 'over'] : ['over', 'under'];
  const answerLabel = (answer: 'over' | 'under') => {
    if (!yesAnswer) return answer === 'over' ? 'Over' : 'Under';
    return answer === yesAnswer ? 'Yes' : 'No';
  };

  const handleAnswerClick = (answer: 'over' | 'under') => {
    if (disabled) {
      return;
    }

    // Toggle logic: click same answer to deselect
    if (selectedAnswer === answer) {
      onAnswerSelect(undefined);
    } else {
      onAnswerSelect(answer);
    }
  };

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={`question-${questionId}`}
      data-test-type="over_under"
    >
      {/* Subtext (optional) — matches other pick components' top-of-card line */}
      {subtext && (
        <span
          data-testid="over-under-subtext"
          className="text-[#ABABAB] font-light block text-center mt-2"
        >
          {subtext}
        </span>
      )}

      {/* Question Text — natural-language sentence like the other types show */}
      {questionText && (
        <span
          data-testid="over-under-question"
          className="text-text-primary font-medium block text-center mb-4"
        >
          {questionText}
        </span>
      )}

      {/* Stat Header (threshold + stat name). Normally hidden — it duplicates the
          question copy ("…over 1.5 Assists?"). Shown as a fallback when there's no
          question text, so a title-less card (admin preview / data missing a title)
          still has a prompt. Force it always-on via SHOW_STAT_HEADER. */}
      {(SHOW_STAT_HEADER || !questionText) && (
        <div className="text-center mb-4 mt-2">
          <div className="text-white font-bold text-xl">{statValue}</div>
          <div className="text-white text-sm mb-2">{statType}</div>
        </div>
      )}

      {/* Dark Container: Jersey, Player Info, and Buttons */}
      <div className="bg-[#121212] rounded-xl p-2 mb-4">
        <div className="grid grid-cols-2 gap-2">
          {/* Left Column: match panel for match-level totals (no player), or
              jersey + player info for player/team over-unders. */}
          {isMatchLevel ? (
            <div className="relative flex flex-col items-center justify-center rounded-lg overflow-hidden py-3 text-center">
              {/* Team logos side by side, when available */}
              {(homeTeamLogoUrl || awayTeamLogoUrl) && (
                <div className="flex items-center gap-2 mb-1">
                  {homeTeamLogoUrl && (
                    <img src={homeTeamLogoUrl} alt={homeTeamName || 'Home'} className="w-8 h-8" />
                  )}
                  <span className="text-[#ABABAB] text-xs">@</span>
                  {awayTeamLogoUrl && (
                    <img src={awayTeamLogoUrl} alt={awayTeamName || 'Away'} className="w-8 h-8" />
                  )}
                </div>
              )}
              {/* Matchup */}
              {(homeTeamName || awayTeamName) && (
                <div className="text-white text-sm font-[600] whitespace-nowrap">
                  {homeTeamName} @ {awayTeamName}
                </div>
              )}
              {/* Match Time */}
              {matchTime && <div className="text-[#ABABAB] text-xs mt-1">{matchTime}</div>}
            </div>
          ) : (
            /* Compact layout (DKPLAT-663): MOBILE ONLY. Keeps the vertical
               jersey-above-text stack but on a smaller jersey with tighter
               spacing, so the card uses roughly half the vertical space and more
               questions fit on a phone screen. The Over/Under buttons opposite
               are h-full, so they shrink to match.
               From `md` up every value reverts to the previous desktop sizing —
               desktop has the room and was not part of the redesign. */
            <div className="relative flex flex-col items-center justify-center rounded-lg overflow-hidden py-1 md:py-0">
              <TeamLogoBackground logoUrl={playerTeamLogoUrl} />
              {/* Jersey with optional team logo overlay */}
              <div className="relative">
                {/* 60px on phones, unchanged 100px from `md` up. */}
                <TeamJersey size="sm" sizeMd="md" jerseyUrl={playerJerseyUrl} />
              </div>
              {/* Player name and position below jersey. `relative` keeps the text
                  above the faded TeamLogoBackground rather than washed into it. */}
              <div className="relative text-center leading-tight md:leading-normal md:mt-1">
                <div className="text-white font-medium text-sm">
                  <span className="z-10 flex justify-center items-center gap-1 px-2">
                    {playerTeamLogoUrl && (
                      <img src={playerTeamLogoUrl} alt="Team" className="w-4 h-4 " />
                    )}
                    <span className="text-white text-sm font-[600] whitespace-nowrap">
                      {playerName}
                    </span>
                    {playerPosition && (
                      <span className="text-[#ABABAB] text-sm">{playerPosition}</span>
                    )}
                  </span>
                </div>
                {/* Team Matchup - show team names if available */}
                {(homeTeamName || awayTeamName) && (
                  <div className="text-[#ABABAB] text-xs md:mt-1">
                    {homeTeamName} @ {awayTeamName}
                  </div>
                )}
                {/* Match Time */}
                {matchTime && <div className="text-[#ABABAB] text-xs">{matchTime}</div>}
              </div>
            </div>
          )}

          {/* Right Column: Over/Under (or Yes/No) Buttons Stacked Vertically */}
          <div className="flex flex-col gap-2 h-full">
            {answerOrder.map(answer => (
              <div key={answer} className="flex-1">
                <AnswerButtonOverUnder
                  answer={answer}
                  label={answerLabel(answer)}
                  isSelected={selectedAnswer === answer}
                  anySelected={selectedAnswer !== undefined}
                  isGraded={isCorrect !== undefined}
                  percentage={answer === 'over' ? overPercentage : underPercentage}
                  onClick={() => handleAnswerClick(answer)}
                  disabled={disabled}
                  isCorrect={selectedAnswer === answer ? isCorrect : undefined}
                  testId={`${questionId}-${answer}`}
                  isDailyDouble={isDailyDouble}
                  className="h-full"
                  showArrow={!yesAnswer}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </QuestionCard>
  );
};

export default OverUnderPick;
